import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Award, ShieldCheck, ShieldOff, Clock, ArrowRight, BadgeCheck, Loader2 } from "lucide-react";

/**
 * Public Registry — Disciplinare DT-RTG-01 § 6.5 Fase 10
 *
 * Public page (NO authentication required). Lists all VIVA ESG certified
 * organizations with search + filters. Powered by /api/public/registry.
 */
export default function PublicRegistry() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [band, setBand] = useState(params.get("band") || "all");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [stats, setStats] = useState({ counts: {}, by_band: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (band && band !== "all") qs.set("band", band);
      if (status && status !== "all") qs.set("status", status);
      qs.set("page", String(page));
      qs.set("page_size", "20");
      const [r, s] = await Promise.all([
        api.get(`/public/registry?${qs.toString()}`),
        api.get(`/public/registry/stats`),
      ]);
      setData(r.data);
      setStats(s.data);
      // Sync URL
      const urlParams = new URLSearchParams();
      if (q) urlParams.set("q", q);
      if (band && band !== "all") urlParams.set("band", band);
      if (status && status !== "all") urlParams.set("status", status);
      if (page > 1) urlParams.set("page", String(page));
      setParams(urlParams, { replace: true });
    } finally { setLoading(false); }
  }, [q, band, status, page, setParams]);

  useEffect(() => { load(); }, [load]);

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    load();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-viva-600 text-white grid place-items-center font-bold text-lg">
              V
            </div>
            <div className="min-w-0">
              <div className="font-heading text-base sm:text-xl font-bold text-slate-900 leading-tight truncate">Registro pubblico</div>
              <div className="text-[11px] sm:text-xs text-slate-500 leading-tight">VIVA ESG · verifica indipendente</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="shrink-0" onClick={() => navigate("/")} data-testid="back-portal-btn">
            <span className="hidden sm:inline">Vai al portale</span>
            <span className="sm:hidden">Portale</span>
            <ArrowRight className="w-4 h-4 ml-1.5 sm:ml-2" />
          </Button>
        </div>
      </header>

      {/* Stats banner */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard icon={Award} label="Totale certificati" value={stats.counts?.total || 0} color="viva" />
          <StatCard icon={ShieldCheck} label="Validi" value={stats.counts?.emesso || 0} color="emerald" />
          <StatCard icon={Clock} label="Sospesi" value={stats.counts?.sospeso || 0} color="amber" />
          <StatCard icon={ShieldOff} label="Scaduti / revocati" value={(stats.counts?.scaduto || 0) + (stats.counts?.revocato || 0)} color="slate" />
        </div>

        {/* Search */}
        <Card className="rounded-2xl border-slate-200 shadow-soft p-5 mb-6">
          <form onSubmit={onSearch} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Cerca</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="registry-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ragione sociale, P.IVA o codice certificato..."
                  className="pl-9 h-11"
                />
              </div>
            </div>
            <div className="w-36">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Banda</label>
              <Select value={band} onValueChange={(v) => { setBand(v); setPage(1); }}>
                <SelectTrigger data-testid="registry-band-filter" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {["V1", "V2", "V3", "V4", "V5", "V6", "V7"].map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Stato</label>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger data-testid="registry-status-filter" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="emesso">Validi</SelectItem>
                  <SelectItem value="sospeso">Sospesi</SelectItem>
                  <SelectItem value="scaduto">Scaduti</SelectItem>
                  <SelectItem value="revocato">Revocati</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="registry-search-btn" className="bg-viva-600 hover:bg-viva-700 text-white h-11 px-5">
              Cerca
            </Button>
          </form>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>
        ) : data.items.length === 0 ? (
          <Card className="p-12 text-center text-slate-500 rounded-2xl">
            <Award className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Nessun certificato trovato per i filtri attuali.
          </Card>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">
              {data.total} {data.total === 1 ? "certificato trovato" : "certificati trovati"}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {data.items.map((it) => (
                <CertCard key={it.certificate_code} item={it} />
              ))}
            </div>
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="registry-prev-page">
                  ← Precedente
                </Button>
                <span className="text-sm text-slate-600">Pagina {data.page} di {data.pages}</span>
                <Button variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="registry-next-page">
                  Successiva →
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <footer className="mt-12 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <div>
            © VIVA ESG · Registro conforme al Disciplinare DT-RTG-01 § 6.5 Fase 10
          </div>
          <div>
            <a href="mailto:tecnico@vivaesg.it" className="hover:text-viva-700">tecnico@vivaesg.it</a>
            {" · "}
            <a href="https://www.vivaesg.it" target="_blank" rel="noreferrer" className="hover:text-viva-700">www.vivaesg.it</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    viva: "bg-viva-50 text-viva-700 border-viva-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Card className={`p-4 rounded-2xl border ${colorMap[color] || colorMap.slate}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-7 h-7 opacity-70" />
        <div>
          <div className="text-2xl font-bold leading-tight">{value}</div>
          <div className="text-xs uppercase tracking-wider opacity-75">{label}</div>
        </div>
      </div>
    </Card>
  );
}

const STATUS_PILL = {
  emesso: { label: "Valido", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  sospeso: { label: "Sospeso", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  scaduto: { label: "Scaduto", cls: "bg-slate-200 text-slate-600 border-slate-300" },
  revocato: { label: "Revocato", cls: "bg-red-100 text-red-700 border-red-200" },
};

function CertCard({ item }) {
  const navigate = useNavigate();
  const meta = STATUS_PILL[item.status] || { label: item.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  const expDate = item.expires_at ? new Date(item.expires_at).toLocaleDateString("it-IT") : "—";
  const issueDate = item.issued_at ? new Date(item.issued_at).toLocaleDateString("it-IT") : "—";
  return (
    <Card
      data-testid={`cert-card-${item.certificate_code}`}
      className="rounded-2xl border-slate-200 shadow-soft hover:shadow-md transition cursor-pointer p-5"
      onClick={() => navigate(`/registro/${item.certificate_code}`)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-heading text-lg font-bold text-slate-900 truncate">
            {item.organization.ragione_sociale || "—"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            P.IVA {item.organization.piva || "—"} · ATECO {item.organization.codice_ateco || "—"}{item.organization.codice_ateco_secondario ? ` / ${item.organization.codice_ateco_secondario}` : ""}
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${meta.cls} shrink-0`}>
          {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Anno assessment" value={item.year} />
        <Field label="Banda VIVA" value={item.band || "—"} />
        <Field label="Emesso" value={issueDate} />
        <Field label="Scadenza" value={expDate} />
      </div>
      {item.snapshot_frozen === false && (
        <div className="mt-2 text-[11px] text-amber-700" data-testid={`registry-snapshot-warning-${item.certificate_code}`}>Valori provvisori: snapshot non ancora congelato</div>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <code className="text-[11px] font-mono text-slate-500">{item.certificate_code}</code>
        <span className="text-xs text-viva-700 font-semibold inline-flex items-center gap-1">
          Verifica <BadgeCheck className="w-3.5 h-3.5" />
        </span>
      </div>
    </Card>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-slate-800 font-medium">{value}</div>
    </div>
  );
}
